// A deliberately small data-fetching hook (no dependency).
//
//   const { data, error, status, isFetching, refetch } =
//     useQuery(['tenants', params], (signal) => api.tenants.list(params, signal), { keepPrevious: true })
//
// • Stale-while-revalidate: cached data renders at once, then refetches.
// • keepPrevious: while the key changes (search, paging), keep showing the
//   last result instead of flashing a loading state.
// • Aborts the in-flight request when the key changes or on unmount.
// • invalidateQueries('tenants') refetches every mounted query whose key
//   starts with that segment. Call it after a write.

import { useCallback, useEffect, useRef, useState } from 'react'
import { isApiError, isSessionEnded } from '@/api/client'

export type QueryKey = ReadonlyArray<unknown>
export type QueryStatus = 'loading' | 'success' | 'error'

const cache = new Map<string, { data: unknown; at: number }>()
const listeners = new Set<(prefix: string) => void>()

export function hashKey(key: QueryKey): string {
  return JSON.stringify(key)
}

export function invalidateQueries(prefix: string) {
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(`["${prefix}"`)) cache.delete(k)
  }
  listeners.forEach((fn) => fn(prefix))
}

export function clearQueryCache() {
  cache.clear()
}

const dataListeners = new Map<string, Set<(data: unknown) => void>>()

/**
 * Replace a query's data with a value the server just returned (e.g. the
 * `{ tenant }` from a write), without a refetch. Mounted queries with this
 * exact key re-render with it.
 */
export function setQueryData<T>(key: QueryKey, data: T) {
  const hashed = hashKey(key)
  cache.set(hashed, { data, at: Date.now() })
  dataListeners.get(hashed)?.forEach((fn) => fn(data))
}

/** The cached data for a key, if any. */
export function getQueryData<T>(key: QueryKey): T | undefined {
  return cache.get(hashKey(key))?.data as T | undefined
}

export interface QueryResult<T> {
  data: T | undefined
  error: unknown
  status: QueryStatus
  /** true while any request for this query is in flight (including background refetches). */
  isFetching: boolean
  refetch: () => void
}

export function useQuery<T>(
  key: QueryKey | null,
  fetcher: (signal: AbortSignal) => Promise<T>,
  opts: { keepPrevious?: boolean } = {},
): QueryResult<T> {
  const hashed = key ? hashKey(key) : null
  const cached = hashed ? (cache.get(hashed) as { data: T } | undefined) : undefined

  const [state, setState] = useState<{ data: T | undefined; error: unknown; status: QueryStatus; isFetching: boolean }>(() => ({
    data: cached?.data,
    error: null,
    status: cached ? 'success' : 'loading',
    isFetching: !!hashed,
  }))
  const [nonce, setNonce] = useState(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const keepPrevious = !!opts.keepPrevious
  const firstSegment = key ? String(key[0]) : null

  // Refetch when someone invalidates our prefix.
  useEffect(() => {
    if (!firstSegment) return
    const fn = (prefix: string) => {
      if (prefix === firstSegment) setNonce((n) => n + 1)
    }
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  }, [firstSegment])

  // The key the current data belongs to. A refetch of the SAME key (after
  // invalidateQueries or refetch) keeps showing that data while it loads,
  // so a write never blanks the page or unmounts what's under it.
  const dataKey = useRef<string | null>(cached ? hashed : null)

  // Accept data pushed by setQueryData for this exact key.
  useEffect(() => {
    if (!hashed) return
    const fn = (data: unknown) => {
      dataKey.current = hashed
      setState({ data: data as T, error: null, status: 'success', isFetching: false })
    }
    let set = dataListeners.get(hashed)
    if (!set) dataListeners.set(hashed, (set = new Set()))
    set.add(fn)
    return () => {
      set!.delete(fn)
      if (set!.size === 0) dataListeners.delete(hashed)
    }
  }, [hashed])

  useEffect(() => {
    if (!hashed) return
    const controller = new AbortController()
    const hit = cache.get(hashed) as { data: T } | undefined
    const sameKey = dataKey.current === hashed
    const keep = keepPrevious || sameKey
    setState((prev) => ({
      data: hit ? hit.data : keep ? prev.data : undefined,
      error: null,
      status: hit || (keep && prev.data !== undefined) ? 'success' : 'loading',
      isFetching: true,
    }))
    fetcherRef
      .current(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return
        cache.set(hashed, { data, at: Date.now() })
        dataKey.current = hashed
        setState({ data, error: null, status: 'success', isFetching: false })
      })
      .catch((error) => {
        if (controller.signal.aborted) return
        // The session ended: stay loading while the route guard swaps to
        // /login?next=… (never an error state on the way out).
        if (isSessionEnded() && isApiError(error) && error.status === 401) return
        setState((prev) => ({ data: keep ? prev.data : undefined, error, status: 'error', isFetching: false }))
      })
    return () => controller.abort()
  }, [hashed, nonce, keepPrevious])

  const refetch = useCallback(() => {
    if (hashed) cache.delete(hashed)
    setNonce((n) => n + 1)
  }, [hashed])

  return { ...state, refetch }
}
